/**
 * The x/4 setup indicator.
 *
 * Appears in the BU list, the BU modal and the overview card. Keeping it one component is what
 * stops the three from drifting apart in colour or wording.
 *
 * @module Pages/BUs/BUManager
 */

import { css } from '@emotion/css';
import type { JSX } from 'react';
import type { SetupProgress as Progress } from '../../Utils/buSetupSteps';
import { getToneColors } from '../../Utils/buSetupSteps';

const containerStyle = css`
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
`;

const labelStyle = css`
  font-size: var(--font-size-2xs);
  font-weight: var(--font-weight-bold);
`;

const barsStyle = css`
  display: flex;
  gap: 3px;
`;

const barStyle = css`
  height: 4px;
  flex: 1;
  border-radius: 2px;
`;

const stepLabelsStyle = css`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-s) var(--space-m);
  padding-top: 2px;
`;

const stepLabelStyle = css`
  font-size: var(--font-size-2xs);
`;

export interface SetupProgressProps {
  progress: Progress;
  /** Show the four step names under the bars — used in the modal, not in the table. */
  showStepLabels?: boolean;
  /** Show what is still missing. */
  showPending?: boolean;
  id?: string;
}

export const SetupProgress = ({
  progress,
  showStepLabels = false,
  showPending = false,
  id,
}: SetupProgressProps): JSX.Element => {
  const { color } = getToneColors(progress.tone);

  return (
    <div id={id} className={containerStyle} title={progress.pendingText}>
      <span className={labelStyle} style={{ color }}>
        {showPending ? `${progress.label} concluídos` : progress.label}
      </span>

      <div className={barsStyle}>
        {progress.steps.map(step => (
          <span
            key={step.key}
            className={barStyle}
            style={{ background: step.done ? color : 'var(--color-border)' }}
          />
        ))}
      </div>

      {showStepLabels ? (
        <div className={stepLabelsStyle}>
          {progress.steps.map(step => (
            <span
              key={step.key}
              className={stepLabelStyle}
              style={{
                color: step.done ? 'var(--color-text)' : 'var(--color-text-muted)',
              }}
            >
              {step.label}
            </span>
          ))}
        </div>
      ) : null}

      {showPending ? (
        <span className={stepLabelStyle} style={{ color: 'var(--color-text-secondary)' }}>
          {progress.pendingText}
        </span>
      ) : null}
    </div>
  );
};
